/**
 * IntakePipeline (Iteration 71)
 * ─────────────────────────────
 * Manager-/Direktor-View aller Innendienst-Projekte (`stage=intake`).
 * Jede Zeile zeigt:
 *   • Kunde + Projekt-Nr.
 *   • Angelegt von (Innendienst-Agent)
 *   • Ampel „Übergabe reif?" — Grün wenn alle Pflichtfelder erfüllt sind,
 *     Amber wenn noch Angaben fehlen (Tooltip listet die Lücken auf).
 *   • Inline-Button „An Vertrieb übergeben" (öffnet Auswahl-Modal)
 *
 * Sichtbarkeit: nur Rollen mit Capability `see_intake_pipeline`
 * (Vertriebsleiter/-Direktor/Admin/Super-Admin) — die Route selbst ist
 * zusätzlich in `App.js` gegated.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, ArrowRight, ShieldCheck, Loader2, Send, X, Info, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";

const READINESS_LABEL = {
  customer_name: "Kunden­name (Vor- + Nachname)",
  customer_contact: "Telefon oder E-Mail",
  customer_address: "PLZ + Ort",
  building_data: "Baujahr oder Wohnfläche",
  tech_or_offer: "Technik-Check oder Angebot",
};

function ReadinessPill({ ready, missing }) {
  const title = ready
    ? "Alle Pflichtfelder erfüllt — kann übergeben werden"
    : "Noch offen: " + (missing || []).map((m) => READINESS_LABEL[m] || m).join(", ");
  return (
    <span
      title={title}
      data-testid={ready ? "readiness-ready" : "readiness-open"}
      className={
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[12px] font-bold uppercase tracking-wider border " +
        (ready
          ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
          : "bg-amber-500/10 text-amber-300 border-amber-500/40")
      }
    >
      {ready ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
      {ready ? "Reif" : "Offen"}
    </span>
  );
}

function HandoverModal({ project, onClose, onDone }) {
  const [targets, setTargets] = useState([]);
  const [targetId, setTargetId] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/users", { params: { roles: "sales_rep,sales_manager,sales_director" } });
        const list = Array.isArray(r.data) ? r.data : (r.data.users || []);
        setTargets(list.filter((u) => u.active !== false && u.status !== "deleted"));
      } catch {
        setTargets([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const submit = async () => {
    if (!targetId) { toast.error("Bitte Außendienstler auswählen"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/projects/${project.id}/handover`, {
        assign_to_user_id: targetId,
        note: note || undefined,
      });
      toast.success(`Übergeben an ${r.data.assigned_to_name}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Übergabe fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="intake-handover-modal">
      <div className="bg-[#18181B] border border-[#00E575]/30 rounded-2xl w-full max-w-lg overflow-hidden">
        <div className="border-b border-zinc-800 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-[#00E575]" />
            <h3 className="font-display text-lg font-semibold text-[#00E575]">An Vertrieb übergeben</h3>
          </div>
          <button onClick={onClose} disabled={saving} className="text-zinc-400 hover:text-zinc-100" data-testid="intake-handover-close">
            <X size={18} />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div className="text-[13px] text-zinc-300">
            <span className="font-semibold text-zinc-100">{project.customer_name || "—"}</span>
            <span className="text-zinc-400"> · {project.project_number}</span>
          </div>
          <div>
            <label className="text-[12px] uppercase tracking-wider font-semibold text-zinc-200 mb-1.5 block">Zuweisen an</label>
            {loading ? (
              <div className="flex items-center gap-2 text-zinc-400 text-[13px]"><Loader2 className="animate-spin" size={14}/> Lade…</div>
            ) : (
              <select
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                data-testid="intake-handover-target"
                className="h-10 w-full bg-[#0F0F11] border border-zinc-800 rounded-lg px-3 text-zinc-100 focus:border-[#00E575] outline-none"
              >
                <option value="">— Bitte wählen —</option>
                {targets.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.first_name} {u.last_name} · {u.role}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label className="text-[12px] uppercase tracking-wider font-semibold text-zinc-200 mb-1.5 block">Übergabe-Notiz (optional)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="intake-handover-note"
              rows={3}
              className="w-full bg-[#0F0F11] border border-zinc-800 rounded-lg px-3 py-2 text-[13px] text-zinc-100 focus:border-[#00E575] outline-none"
              placeholder="Was sollte der Kollege vor dem Anruf wissen?"
            />
          </div>
          {!project.handover_ready && (
            <div className="flex items-start gap-2 rounded-lg bg-amber-500/10 border border-amber-500/40 px-3 py-2">
              <Info size={14} className="text-amber-300 mt-0.5 shrink-0" />
              <div className="text-[12px] text-amber-200 font-medium leading-snug">
                Achtung — noch nicht alle Pflichtfelder erfüllt: {(project.missing || []).map((m) => READINESS_LABEL[m] || m).join(", ")}. Übergabe trotzdem möglich, der Vertrieb muss diese Angaben nachfassen.
              </div>
            </div>
          )}
        </div>
        <div className="border-t border-zinc-800 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} disabled={saving} className="h-9 px-4 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[12px] font-medium text-zinc-200 disabled:opacity-40">
            Abbrechen
          </button>
          <button
            onClick={submit}
            disabled={saving || loading}
            data-testid="intake-handover-submit"
            className="h-9 px-4 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12px] inline-flex items-center gap-1.5 hover:bg-[#00C964] disabled:opacity-40"
          >
            {saving ? <Loader2 className="animate-spin" size={12} /> : <Send size={12} />}
            Übergeben
          </button>
        </div>
      </div>
    </div>
  );
}

export default function IntakePipeline() {
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [readyFilter, setReadyFilter] = useState(() => {
    // Deep-Link vom Header-Badge: `?ready=yes` startet direkt im „Reif"-Filter
    const q = searchParams.get("ready");
    return q === "yes" || q === "no" ? q : "all";
  });
  const [modalProject, setModalProject] = useState(null);

  const load = () => {
    setLoading(true);
    const params = {};
    if (readyFilter !== "all") params.ready = readyFilter;
    if (search) params.search = search;
    api.get("/intake-pipeline", { params })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch((e) => toast.error(e.response?.data?.detail || "Laden fehlgeschlagen"))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [readyFilter]);

  const readyCount = useMemo(() => items.filter((x) => x.handover_ready).length, [items]);

  return (
    <div className="min-h-screen bg-[#09090B]">
      <AppHeader title="Intake-Pipeline" />
      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="mb-6">
          <p className="font-mono text-xs text-[#00E575] tracking-[0.3em] uppercase mb-2">Innendienst → Außendienst</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Intake-Pipeline</h1>
          <p className="text-zinc-400 mt-1 text-sm font-medium">
            {loading ? "Lade…" : (
              <>
                <span data-testid="intake-total-count" className="text-zinc-200">{items.length} Projekte</span>
                {" · "}
                <span data-testid="intake-ready-count" className="text-emerald-300">{readyCount} übergabe-reif</span>
              </>
            )}
          </p>
        </div>

        {/* Filter row */}
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
            <input
              data-testid="intake-search-input"
              type="text"
              placeholder="Kunde, Ort, PLZ, Projekt-Nr…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              className="h-12 w-full bg-[#18181B] border border-zinc-800 rounded-xl pl-12 pr-4 text-zinc-50 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] transition outline-none"
            />
          </div>
          <div className="inline-flex bg-[#18181B] border border-zinc-800 rounded-xl overflow-hidden h-12">
            {[
              { key: "all", label: "Alle" },
              { key: "yes", label: "Reif" },
              { key: "no", label: "Offen" },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setReadyFilter(t.key)}
                data-testid={`intake-filter-${t.key}`}
                className={
                  "px-4 h-full text-[13px] font-semibold transition " +
                  (readyFilter === t.key
                    ? "bg-[#00E575] text-[#09090B]"
                    : "text-zinc-300 hover:bg-zinc-800")
                }
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-zinc-300 text-center font-medium py-12">Lade Intake-Pipeline…</div>
        ) : items.length === 0 ? (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-12 text-center">
            <p className="text-zinc-400">Keine Intake-Projekte in dieser Ansicht.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((p) => (
              <div
                key={p.id}
                data-testid={`intake-item-${p.id}`}
                className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 hover:border-zinc-700 transition"
              >
                <div className="flex items-center justify-between gap-3">
                  <Link to={`/projects/${p.id}`} className="min-w-0 flex-1 group">
                    <div className="font-medium truncate flex items-center gap-2">
                      <span className="truncate group-hover:text-[#00E575] transition">{p.customer_name || "—"}</span>
                      <ReadinessPill ready={p.handover_ready} missing={p.missing} />
                    </div>
                    <div className="text-xs text-zinc-400 font-mono mt-0.5">
                      {p.project_number} · {p.customer_zip || ""} {p.customer_city || ""}
                    </div>
                    <div className="text-[12px] text-zinc-500 mt-1 font-medium">
                      Angelegt von: <span className="text-zinc-300">{p.owner_name || "—"}</span>
                      {p.has_technical_check && <span className="text-emerald-400 ml-2">· Tech-Check ✓</span>}
                      {p.has_offer && <span className="text-emerald-400 ml-2">· Angebot ✓</span>}
                    </div>
                  </Link>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => setModalProject(p)}
                      data-testid={`intake-handover-btn-${p.id}`}
                      className="hidden md:inline-flex h-9 px-3 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[12px] items-center gap-1.5 hover:bg-[#00C964] transition active:scale-95"
                    >
                      <Send size={12} /> Übergeben
                    </button>
                    <Link
                      to={`/projects/${p.id}`}
                      data-testid={`intake-open-${p.id}`}
                      className="inline-flex items-center gap-1 h-9 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[12px] font-medium text-zinc-200"
                    >
                      Öffnen <ArrowRight size={12} />
                    </Link>
                  </div>
                </div>
                {/* Mobile handover button */}
                <button
                  onClick={() => setModalProject(p)}
                  data-testid={`intake-handover-btn-mobile-${p.id}`}
                  className="md:hidden mt-3 w-full h-10 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] inline-flex items-center justify-center gap-1.5 hover:bg-[#00C964] transition"
                >
                  <Send size={13} /> An Vertrieb übergeben
                </button>
              </div>
            ))}
          </div>
        )}
      </main>
      {modalProject && (
        <HandoverModal
          project={modalProject}
          onClose={() => setModalProject(null)}
          onDone={() => { setModalProject(null); load(); }}
        />
      )}
    </div>
  );
}
